import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable, timeout } from 'rxjs';
import { AuthApiError, mapAuthError } from './auth.errors';
import { AuthSessionStore, normalizeExpiresAt } from './auth-session.store';
import { AUTH_API_URL, SKIP_ACCESS_TOKEN } from './auth.tokens';
import {
  ApiResponse,
  AuthBootstrapResponse,
  AuthContext,
  AuthTokenResponse,
  GuestTokenResponse,
} from './auth.types';

const REQUEST_TIMEOUT_MS = 15_000;
const PUBLIC_REQUEST_CONTEXT = new HttpContext().set(SKIP_ACCESS_TOKEN, true);
const REALM_LOGIN_CONTEXTS = new Set<AuthContext>([
  'clients',
  'advisors',
  'suppliers',
  'real-estates',
]);

@Injectable({ providedIn: 'root' })
export class AuthApiService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(AUTH_API_URL).replace(/\/+$/, '');
  private readonly session = inject(AuthSessionStore);

  async login(
    context: AuthContext,
    email: string,
    password: string,
  ): Promise<AuthBootstrapResponse> {
    this.session.clear();
    try {
      const guest = await this.requestGuestToken();
      const token = await this.request<AuthTokenResponse>(
        this.http.post<ApiResponse<AuthTokenResponse>>(
          this.loginUrl(context),
          { email: email.trim().toLowerCase(), password },
          {
            context: PUBLIC_REQUEST_CONTEXT,
            headers: new HttpHeaders({
              Authorization: `Bearer ${guest.guestToken}`,
            }),
            withCredentials: true,
          },
        ),
      );
      assertUsableToken(token);
      this.session.setToken(token);
      return REALM_LOGIN_CONTEXTS.has(context)
        ? await this.bootstrap(context)
        : await this.loadWelcome();
    } catch (error) {
      this.session.clear();
      throw mapAuthError(error);
    }
  }

  async establishPortalSession(
    context: AuthContext,
  ): Promise<AuthBootstrapResponse> {
    try {
      const token = await this.request<AuthTokenResponse>(
        this.http.post<ApiResponse<AuthTokenResponse>>(
          `${this.apiUrl}/portal-token/${context}`,
          null,
          {
            context: PUBLIC_REQUEST_CONTEXT,
            withCredentials: true,
          },
        ),
      );
      assertUsableToken(token);
      this.session.setToken(token);
      return context === 'auth' || context === 'welcome'
        ? await this.loadWelcome()
        : await this.bootstrap(context);
    } catch (error) {
      this.session.clear();
      throw mapAuthError(error);
    }
  }

  /** Reemite el access token con la sesión central, sin rotar la cookie. */
  async reissueToken(context: AuthContext): Promise<void> {
    await this.requestSessionToken(`${this.apiUrl}/portal-token/${context}`);
  }

  private async requestSessionToken(url: string): Promise<void> {
    const token = await this.request<AuthTokenResponse>(
      this.http.post<ApiResponse<AuthTokenResponse>>(url, null, {
        context: PUBLIC_REQUEST_CONTEXT,
        withCredentials: true,
      }),
    );
    assertUsableToken(token);
    this.session.setToken(token);
  }

  async loadWelcome(): Promise<AuthBootstrapResponse> {
    try {
      const bootstrap = await this.request<AuthBootstrapResponse>(
        this.http.get<ApiResponse<AuthBootstrapResponse>>(
          `${this.apiUrl}/welcome`,
        ),
      );
      this.session.setBootstrap(bootstrap);
      return bootstrap;
    } catch (error) {
      this.session.clear();
      throw mapAuthError(error);
    }
  }

  async bootstrap(context: AuthContext): Promise<AuthBootstrapResponse> {
    try {
      const bootstrap = await this.request<AuthBootstrapResponse>(
        this.http.get<ApiResponse<AuthBootstrapResponse>>(
          `${this.apiUrl}/me/bootstrap/${context}`,
        ),
      );
      this.session.setBootstrap(bootstrap);
      return bootstrap;
    } catch (error) {
      this.session.clear();
      throw mapAuthError(error);
    }
  }

  async logout(): Promise<void> {
    try {
      if (this.session.accessToken()) {
        await this.request<string>(
          this.http.post<ApiResponse<string>>(`${this.apiUrl}/logout`, null, {
            withCredentials: true,
          }),
        );
      }
    } catch {
      // La limpieza local es obligatoria aunque el cierre remoto falle.
    } finally {
      this.session.clear();
    }
  }

  private loginUrl(context: AuthContext): string {
    return REALM_LOGIN_CONTEXTS.has(context)
      ? `${this.apiUrl}/login/${context}`
      : `${this.apiUrl}/login`;
  }

  private async requestGuestToken(): Promise<GuestTokenResponse> {
    return this.request<GuestTokenResponse>(
      this.http.post<ApiResponse<GuestTokenResponse>>(
        `${this.apiUrl}/guest-token`,
        null,
        { context: PUBLIC_REQUEST_CONTEXT },
      ),
    );
  }

  private async request<T>(source: Observable<ApiResponse<T>>): Promise<T> {
    try {
      const response = await firstValueFrom(
        source.pipe(timeout(REQUEST_TIMEOUT_MS)),
      );
      return response.data;
    } catch (error) {
      throw mapAuthError(error);
    }
  }
}

function assertUsableToken(token: AuthTokenResponse): void {
  const expiresAt = normalizeExpiresAt(token.expiresAt);
  if (
    !token.accessToken ||
    !Number.isFinite(expiresAt) ||
    expiresAt <= Date.now()
  ) {
    throw new AuthApiError('unexpected', 0);
  }
}
